import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import type { ReactNode } from 'react'
import { Icon } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'

export type ZoneCountOption = {
  zoneCount: number
  name: string
  detail: string
  recommended: boolean
  disabled: boolean
}

const OptionContent = ({
  name,
  detail,
  recommended,
  recommendedLabel
}: {
  name: string
  detail: string
  recommended: boolean
  recommendedLabel: string
}) => (
  <span className="flex w-full items-center justify-between gap-2">
    <span className="pan-body-bold flex items-center gap-2">
      {name}
      {recommended && <span className="pan-detail text-green-700">{recommendedLabel}</span>}
    </span>
    <span className="pan-detail text-gray-500">{detail}</span>
  </span>
)

export const ZoneCountSelect = ({
  label,
  recommendedLabel,
  options,
  value,
  custom,
  onChange
}: {
  label: ReactNode
  recommendedLabel: string
  options: ZoneCountOption[]
  value: number | undefined
  custom: { name: string; detail: string } | undefined
  onChange: (zoneCount: number) => void
}) => {
  const selected = options.find((option) => option.zoneCount === value)
  const shown = custom ?? selected

  return (
    <Listbox value={value ?? null} onChange={(next) => next !== null && onChange(next)}>
      <div className="w-full">
        <Label className="pan-label mb-2 block">{label}</Label>
        <ListboxButton className="group/select flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 text-left data-[open]:rounded-b-none data-[open]:border-green">
          {shown !== undefined && (
            <OptionContent
              name={shown.name}
              detail={shown.detail}
              recommended={custom === undefined && selected?.recommended === true}
              recommendedLabel={recommendedLabel}
            />
          )}
          <Icon
            icon={SpriteMap.ChevronIcon}
            className="size-3 flex-none rotate-180 text-gray-400 group-data-[open]/select:rotate-0 group-data-[open]/select:text-green-500"
          />
        </ListboxButton>
        <ListboxOptions
          modal={false}
          anchor={{ to: 'bottom start', gap: '-1px' }}
          className="z-[100] flex w-[--button-width] flex-col rounded-b-lg border border-t-0 border-green bg-white focus:outline-none"
        >
          {options.map((option) => (
            <ListboxOption
              key={option.zoneCount}
              value={option.zoneCount}
              disabled={option.disabled}
              className="cursor-pointer px-3 py-2 data-[disabled]:cursor-not-allowed data-[focus]:bg-gray-50 data-[selected]:bg-green-50 data-[disabled]:opacity-50"
            >
              <OptionContent
                name={option.name}
                detail={option.detail}
                recommended={option.recommended}
                recommendedLabel={recommendedLabel}
              />
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  )
}
